import React from "react";
import { Check, Loader2, FileCode2, Wand2 } from "lucide-react";
import { parseStream } from "../../lib/streamParse";

const Status = ({ children }) => (
  <div className="flex items-center gap-2 text-emerald-600 font-mono text-xs py-1.5 px-2.5 bg-emerald-50 border border-emerald-200 rounded-lg w-fit" data-testid="streaming-status">
    <Loader2 size={12} className="animate-spin" />{children}
  </div>
);

const PlanStream = ({ raw }) => {
  const steps = [...raw.matchAll(/<step>([\s\S]*?)<\/step>/g)].map((m) => m[1].trim());
  const sm = raw.match(/<summary>([\s\S]*?)(<\/summary>|$)/);
  return (
    <div className="space-y-3" data-testid="streaming-plan">
      <Status>Drafting a plan…</Status>
      {sm && <p className="text-sm text-slate-600 leading-relaxed">{sm[1].replace(/<[^>]*$/, "").trim()}</p>}
      {steps.map((s, i) => <p key={i} className="text-sm text-slate-500 flex gap-2"><span className="font-mono text-slate-400">{i + 1}.</span>{s}</p>)}
    </div>
  );
};

const ImagesStage = ({ paths = [] }) => (
  <div className="space-y-2" data-testid="streaming-images">
    <Status>Creating {paths.length} AI image{paths.length === 1 ? "" : "s"}…</Status>
    <div className="flex flex-wrap gap-2">
      {paths.map((p) => <span key={p} className="flex items-center gap-1.5 px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-[11px] font-mono text-amber-700"><Wand2 size={11} />{p}</span>)}
    </div>
  </div>
);

export const StreamingView = ({ raw, plan, stage, pendingImages }) => {
  if (plan) return <PlanStream raw={raw} />;
  if (stage === "images") return <ImagesStage paths={pendingImages} />;
  const { summary, files, current } = parseStream(raw);
  return (
    <div className="space-y-3" data-testid="streaming-view">
      <Status>{files.length === 0 ? "Thinking it through…" : current ? `Writing ${current.path}` : "Finishing up…"}</Status>
      {summary && <p className="text-sm text-slate-600 leading-relaxed">{summary}</p>}
      {files.length > 0 && (
        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-1 text-xs font-mono" data-testid="streaming-files">
          {files.map((f) => (
            <div key={f.path} className={`flex items-center gap-2 ${f.done ? "text-emerald-600" : "text-amber-600"}`}>
              {f.done ? <Check size={12} /> : <Loader2 size={12} className="animate-spin" />}
              <FileCode2 size={12} className="text-slate-500" /> {f.path}
              <span className="ml-auto text-slate-400">{f.lines} lines</span>
            </div>
          ))}
        </div>
      )}
      {current && (
        <pre className="max-h-48 overflow-hidden rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] leading-relaxed text-emerald-700 whitespace-pre-wrap break-all caret">
          {current.tail}
        </pre>
      )}
    </div>
  );
};

export default StreamingView;
